import * as React from 'react';
import { graphql, useStaticQuery, Link, navigate } from 'gatsby';
import useGoogleAnalytics from 'analytics/hooks/useGoogleAnalytics';
import { useABTesting } from 'core/components/ABTestingProvider/ABTestingProvider';
import CallToAction from 'core/components/CallToAction';
import { useIntl } from 'react-intl';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import * as styles from './BookingButton.module.scss';

export type LinkBlockProps = {
    text: React.ReactNode;
    editMode?: boolean;
};

function BookingButton({ text, editMode }: LinkBlockProps): React.ReactElement {
    const { trackWithAnyGoogleAnalytics } = useGoogleAnalytics();
    const { getAllPageVariations } = useABTesting();

    const intl = useIntl();

    const pageVariations = getAllPageVariations();

    const isVariantB = pageVariations?.includes('blogCTATestB');
    const isVariantC = pageVariations?.includes('blogCTATestC');

    const event = isVariantB
        ? 'blogTest2_imageCTAVarB'
        : isVariantC
          ? 'blogTest2_CTA_illustrationCTAVarC'
          : 'blogTest2_redCTAVarA';

    const handleClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
        e.preventDefault();
        trackWithAnyGoogleAnalytics(false, 'event', event);
        requestAnimationFrame(() => navigate('/booking/city'));
    };

    const imageData = useStaticQuery(graphql`
        query {
            variantBDesktop: file(
                relativePath: { eq: "blogTest2_varB_desktop.png" }
            ) {
                childImageSharp {
                    gatsbyImageData(height: 194)
                }
            }
            variantBMobile: file(
                relativePath: { eq: "blogTest2_varB_mobile.png" }
            ) {
                childImageSharp {
                    gatsbyImageData(height: 153)
                }
            }
            variantCDesktop: file(
                relativePath: { eq: "blogTest2_varC_desktop.png" }
            ) {
                childImageSharp {
                    gatsbyImageData(height: 194)
                }
            }
            variantCMobile: file(
                relativePath: { eq: "blogTest2_varC_mobile.png" }
            ) {
                childImageSharp {
                    gatsbyImageData(height: 153)
                }
            }
        }
    `);

    const selectedDesktopImage = getImage(
        isVariantC ? imageData.variantCDesktop : imageData.variantBDesktop,
    );
    const selectedMobileImage = getImage(
        isVariantC ? imageData.variantCMobile : imageData.variantBMobile,
    );

    return (
        <div className={styles.root}>
            {isVariantB || isVariantC ? (
                <div className={styles.bookingBanner}>
                    <div className={styles.bannerBody}>
                        <div className={styles.bannerTitle}>
                            {intl.formatMessage({
                                id: 'blog.banner.text',
                            })}
                            <span className={styles.symbol}>®</span>
                        </div>
                        <div>
                            <CallToAction
                                Component={editMode ? 'span' : Link}
                                to="/booking/city"
                                variant="contained"
                                className={styles.smallRedButton}
                                onClick={handleClick}
                            >
                                {intl.formatMessage({
                                    id: 'blog.banner.cta',
                                })}
                            </CallToAction>
                        </div>
                    </div>
                    <div className={styles.illustration}>
                        {selectedDesktopImage && (
                            <GatsbyImage
                                image={selectedDesktopImage}
                                alt=""
                                className={styles.desktopImage}
                            />
                        )}
                        {selectedMobileImage && (
                            <GatsbyImage
                                image={selectedMobileImage}
                                alt=""
                                className={styles.mobileImage}
                            />
                        )}
                    </div>
                </div>
            ) : (
                <CallToAction
                    Component={editMode ? 'span' : Link}
                    to="/booking/city"
                    variant="contained"
                    className={styles.redButton}
                    onClick={handleClick}
                >
                    {text}
                </CallToAction>
            )}
        </div>
    );
}

export default BookingButton;
